import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, CreditCard, MessageCircle, Star, ScrollText, LayoutDashboard,
  Search, Loader2, ArrowLeft, Ban, Gift, ShieldCheck, UserPlus, Copy, Check, Megaphone, Mail, Send, Rocket, Wallet,
  Target, Download, MessageSquareText, Upload, Trash2, X, ChevronRight,
  Clock, History, RefreshCw, Circle, Wand2,
} from "lucide-react";
import { api, vibrate, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ComposedChart, Bar, Line, Area, AreaChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APP_URL } from "@/config";
import { PLATFORMS, STATUS_META, inviteLink, buildDM } from "@/lib/prospect";
import ContentStudio from "@/components/ContentStudio";

const TABS = [
  { k: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
  { k: "users", label: "Utilisateurs", icon: Users },
  { k: "payments", label: "Paiements", icon: CreditCard },
  { k: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { k: "reviews", label: "Avis", icon: Star },
  { k: "invites", label: "Invitations", icon: UserPlus },
  { k: "ambassadors", label: "Ambassadrices", icon: Megaphone },
  { k: "creators", label: "Créateurs", icon: Rocket },
  { k: "prospects", label: "Prospection", icon: Target },
  { k: "content", label: "Contenu", icon: Wand2 },
  { k: "audit", label: "Audit", icon: ScrollText },
];

const TIER_COLORS = {
  free: "text-zinc-400", basic: "text-sky-400", pro: "text-pink-400",
  docteur: "text-amber-400", comp: "text-emerald-400",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && user && !user.is_admin) navigate("/app");
  }, [loading, user, navigate]);

  if (loading || !user) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="animate-spin text-pink-400" /></div>;
  if (!user.is_admin) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-page">
      <header className="sticky top-0 z-10 bg-zinc-950/90 backdrop-blur border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/app")} className="text-zinc-400 hover:text-white" data-testid="admin-back"><ArrowLeft size={18} /></button>
        <ShieldCheck size={18} className="text-emerald-400" />
        <h1 className="font-display font-black">Admin · DocLove</h1>
        <span className="ml-auto text-xs text-zinc-500 truncate max-w-[40vw]">{user.email}</span>
      </header>

      <div className="flex gap-1 overflow-x-auto px-3 py-2 border-b border-zinc-900">
        {TABS.map(({ k, label, icon: Icon }) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-pink-500/15 text-pink-300 border border-pink-500/30" : "text-zinc-400 hover:text-zinc-200"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="max-w-5xl mx-auto p-4">
        {tab === "overview" && <Overview />}
        {tab === "users" && <UsersTab />}
        {tab === "payments" && <SimpleList endpoint="/admin/payments" field="payments" render={(p) => (
          <Row key={p.session_id} left={p.email || "—"} sub={p.plan} right={`${p.amount} ${p.currency}`} meta={fmt(p.at)} />
        )} empty="Aucun paiement." />}
        {tab === "whatsapp" && <SimpleList endpoint="/admin/whatsapp" field="sessions" render={(s, i) => (
          <Row key={i} left={s.email || s.user_id} sub={s.phone || "—"} right={s.status || "?"} meta={fmt(s.updated_at)} />
        )} empty="Aucune session WhatsApp." />}
        {tab === "reviews" && <SimpleList endpoint="/admin/reviews" field="reviews" render={(r, i) => (
          <div key={i} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid="admin-review">
            <div className="flex items-center gap-2">
              <span className={`font-bold ${r.rating >= 4 ? "text-emerald-400" : "text-red-400"}`}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
              <span className="text-xs text-zinc-500">{r.email}</span>
              {!r.is_public && r.rating < 4 && <span className="ml-auto text-[10px] font-bold text-red-300 bg-red-500/10 border border-red-500/20 rounded-full px-2 py-0.5">PRIVÉ</span>}
            </div>
            {r.testimonial && <p className="text-sm text-zinc-300 mt-1">"{r.testimonial}"</p>}
            {r.feedback && <p className="text-sm text-amber-200/90 mt-1">⚠️ {r.feedback}</p>}
            <p className="text-[11px] text-zinc-600 mt-1">{fmt(r.created_at)}</p>
          </div>
        )} empty="Aucun avis." />}
        {tab === "invites" && <InvitesTab />}
        {tab === "ambassadors" && <AmbassadorsTab />}
        {tab === "creators" && <CreatorsTab />}
        {tab === "prospects" && <ProspectsTab />}
        {tab === "content" && <ContentStudio />}
        {tab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

const fmt = (d) => { try { return new Date(d).toLocaleString("fr-FR"); } catch { return "—"; } };
const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function TabInfo({ title, children }) {
  return (
    <div className="rounded-2xl bg-zinc-900/50 border border-zinc-800 p-3.5 mb-4" data-testid="tab-info">
      <p className="text-xs font-bold text-zinc-200 mb-1">{title}</p>
      <p className="text-[11px] leading-relaxed text-zinc-400">{children}</p>
    </div>
  );
}

const relTime = (d) => {
  if (!d) return "jamais vu";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (isNaN(s)) return "—";
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  if (s < 2592000) return `il y a ${Math.floor(s / 86400)} j`;
  return fmt(d);
};

function Row({ left, sub, right, meta }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-zinc-900 border border-zinc-800 p-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{left}</p>
        <p className="text-xs text-zinc-500 truncate">{sub}</p>
      </div>
      <div className="text-right shrink-0">
        {right && <p className="text-sm font-bold">{right}</p>}
        <p className="text-[11px] text-zinc-600">{meta}</p>
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [dau, setDau] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/growth").then((r) => setGrowth(r.data.series)).catch(() => {});
    api.get("/admin/analytics/dau?days=30").then((r) => setDau(r.data)).catch(() => {});
  }, []);
  if (!s) return <Loader2 className="animate-spin text-pink-400 mx-auto mt-10" />;
  const cards = [
    { label: "Utilisateurs", value: s.total_users },
    { label: "🟢 En ligne", value: s.online_now ?? 0 },
    { label: "Actifs 24h", value: s.active_24h ?? 0 },
    { label: "MRR estimé", value: `${s.mrr} €` },
    { label: "Payants", value: s.paying_users },
    { label: "Analyses", value: s.total_analyses },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{c.label}</p>
            <p className="font-display text-2xl font-black mt-1">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
        <p className="text-sm font-bold mb-2">Répartition par tier</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(s.tiers || {}).map(([t, n]) => (
            <span key={t} className={`text-sm font-semibold ${TIER_COLORS[t] || "text-zinc-300"}`}>{t}: {n}</span>
          ))}
        </div>
      </div>

      {growth && growth.length > 0 && (
        <div className="mt-4 rounded-2xl bg-zinc-900 border border-zinc-800 p-4" data-testid="admin-growth-chart">
          <p className="text-sm font-bold mb-3">Inscriptions & MRR · 30 jours</p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={growth} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} interval={4} tickLine={false} axisLine={false} />
              <YAxis yAxisId="l" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, fontSize: 12 }}
                formatter={(v, n) => [n === "mrr" ? `${v} €` : v, n === "mrr" ? "MRR" : "Inscriptions"]} />
              <Bar yAxisId="l" dataKey="signups" fill="#ec4899" radius={[3, 3, 0, 0]} maxBarSize={14} />
              <Line yAxisId="r" type="monotone" dataKey="mrr" stroke="#34d399" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-pink-500" /> Inscriptions/jour</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> MRR cumulé (€)</span>
          </div>
        </div>
      )}

      {dau && dau.series && dau.series.length > 0 && (
        <div className="mt-4 rounded-2xl bg-zinc-900 border border-zinc-800 p-4" data-testid="admin-dau-chart">
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-sm font-bold">Utilisateurs actifs / jour · 30 jours</p>
            <p className="text-xs text-zinc-500">Aujourd'hui <span className="text-emerald-400 font-bold">{dau.today}</span> · pic <span className="text-zinc-300 font-bold">{dau.peak}</span></p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={dau.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} interval={4} tickLine={false} axisLine={false}
                tickFormatter={(d) => (d || "").slice(5)} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, fontSize: 12 }}
                formatter={(v) => [v, "Actifs"]} />
              <Area type="monotone" dataKey="count" stroke="#38bdf8" strokeWidth={2.5} fill="url(#dauFill)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-zinc-500 mt-2">Un utilisateur est « actif » s'il s'est connecté ou a utilisé l'app ce jour-là.</p>
        </div>
      )}
    </div>
  );
}

function ActivityModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/users/${userId}/activity`).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [userId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="activity-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <History size={18} className="text-pink-400" />
          <h3 className="font-bold">Historique de connexion</h3>
          <button onClick={onClose} className="ml-auto text-zinc-500 hover:text-white" data-testid="activity-close"><X size={18} /></button>
        </div>
        {!data ? <Loader2 className="animate-spin text-pink-400 mx-auto my-8" /> : data.error ? (
          <p className="text-sm text-zinc-500">Impossible de charger.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 ${data.online ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
                <Circle size={8} className={data.online ? "fill-emerald-400 text-emerald-400" : "fill-zinc-500 text-zinc-500"} />
                {data.online ? "En ligne" : "Hors ligne"}
              </span>
              <span className="text-xs text-zinc-500">Vu {relTime(data.last_seen_at)}</span>
            </div>
            <p className="text-sm truncate"><span className="text-zinc-500">Compte :</span> {data.email}</p>
            <div className="grid grid-cols-3 gap-2">
              {[["Connexions", data.login_count], ["Sessions actives", data.active_sessions], ["Inscrit", relTime(data.created_at)]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-zinc-950 border border-zinc-800 p-2.5 text-center">
                  <p className="font-display text-lg font-black">{v}</p>
                  <p className="text-[10px] text-zinc-500">{l}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-1.5">Dernières connexions</p>
              <div className="space-y-1" data-testid="activity-logins">
                {(data.logins || []).map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-zinc-300 border-b border-zinc-800/60 py-1.5">
                    <Clock size={12} className="text-zinc-600" /> {fmt(l)} <span className="text-zinc-600 ml-auto">{relTime(l)}</span>
                  </div>
                ))}
                {!(data.logins || []).length && <p className="text-xs text-zinc-500">Aucune connexion enregistrée.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [activityFor, setActivityFor] = useState(null);

  const load = useCallback(async (query = "", onlyOnline = false, silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get(`/admin/users?q=${encodeURIComponent(query)}${onlyOnline ? "&online=1" : ""}`); setUsers(r.data.users); }
    catch { if (!silent) toast.error("Erreur chargement"); } finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => { load(q, onlineOnly); /* eslint-disable-next-line */ }, [load, onlineOnly]);
  // Live refresh every 20s (silent) to keep online status current.
  useEffect(() => {
    const id = setInterval(() => load(q, onlineOnly, true), 20000);
    return () => clearInterval(id);
  }, [load, q, onlineOnly]);

  const onlineCount = users.filter((u) => u.online).length;

  const setTier = async (u, tier) => {
    setBusy(u.user_id); vibrate();
    try { await api.post(`/admin/users/${u.user_id}/tier`, { tier }); toast.success(`${u.email} → ${tier}`); await load(q, onlineOnly); }
    catch { toast.error("Échec"); } finally { setBusy(null); }
  };
  const ban = async (u) => {
    if (!window.confirm(`${u.banned ? "Débannir" : "Bannir"} ${u.email} ?`)) return;
    setBusy(u.user_id);
    try { await api.post(`/admin/users/${u.user_id}/ban`, { banned: !u.banned }); toast.success("OK"); await load(q, onlineOnly); }
    catch { toast.error("Échec"); } finally { setBusy(null); }
  };
  const grant = async (u) => {
    const days = parseInt(window.prompt("Combien de jours de Pro offerts ?", "7") || "0", 10);
    if (!days) return;
    setBusy(u.user_id);
    try { await api.post(`/admin/users/${u.user_id}/grant`, { days, tier: "pro" }); toast.success(`+${days}j Pro à ${u.email}`); await load(q, onlineOnly); }
    catch { toast.error("Échec"); } finally { setBusy(null); }
  };

  return (
    <div>
      {activityFor && <ActivityModal userId={activityFor} onClose={() => setActivityFor(null)} />}

      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="users-live-bar">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-3 py-1.5 text-sm font-bold text-emerald-300">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
          {onlineCount} en ligne
        </span>
        <button data-testid="users-online-filter" onClick={() => setOnlineOnly((v) => !v)}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${onlineOnly ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>
          {onlineOnly ? "✓ Actifs uniquement" : "Actifs uniquement"}
        </button>
        <button data-testid="users-refresh" onClick={() => load(q, onlineOnly)} className="rounded-full px-3 py-1.5 text-xs font-semibold border border-zinc-800 text-zinc-400 hover:text-white flex items-center gap-1">
          <RefreshCw size={12} /> Rafraîchir
        </button>
        <span className="text-[11px] text-zinc-600 ml-auto">Actualisé auto · en ligne = actif &lt; 5 min</span>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(q, onlineOnly); }} className="flex gap-2 mb-3">
        <div className="flex-1 flex items-center gap-2 rounded-xl bg-zinc-900 border border-zinc-800 px-3">
          <Search size={15} className="text-zinc-500" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher par email / nom"
            className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
        </div>
        <button className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white font-bold px-4 text-sm">OK</button>
      </form>
      {loading ? <Loader2 className="animate-spin text-pink-400 mx-auto mt-6" /> : (
        <div className="space-y-2" data-testid="admin-users-list">
          {users.map((u) => (
            <div key={u.user_id} className={`rounded-xl border p-3 ${u.banned ? "bg-red-500/5 border-red-500/30" : "bg-zinc-900 border-zinc-800"}`} data-testid={`admin-user-${u.user_id}`}>
              <div className="flex items-center gap-2">
                <span title={u.online ? "En ligne" : "Hors ligne"} data-testid={`user-status-${u.user_id}`}
                  className="relative flex h-2.5 w-2.5 shrink-0">
                  {u.online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />}
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${u.online ? "bg-emerald-400" : "bg-zinc-600"}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{u.email} {u.banned && <span className="text-red-400 text-xs">(banni)</span>}</p>
                  <p className="text-xs text-zinc-500">
                    <span className={TIER_COLORS[u.tier]}>{u.tier}</span> · {u.referral_count} parrainages · <span className={u.online ? "text-emerald-400" : ""}>vu {relTime(u.last_seen_at)}</span>
                  </p>
                </div>
                <button data-testid={`admin-activity-${u.user_id}`} onClick={() => setActivityFor(u.user_id)}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1 shrink-0">
                  <History size={12} /> Historique
                </button>
                {busy === u.user_id && <Loader2 size={15} className="animate-spin text-pink-400" />}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {["free", "basic", "pro", "docteur", "comp"].map((t) => (
                  <button key={t} data-testid={`admin-tier-${u.user_id}-${t}`} disabled={busy === u.user_id} onClick={() => setTier(u, t)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold border ${u.tier === t ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>{t}</button>
                ))}
                <button data-testid={`admin-grant-${u.user_id}`} onClick={() => grant(u)} className="rounded-full px-2.5 py-1 text-xs font-semibold border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1"><Gift size={11} /> Offrir</button>
                <button data-testid={`admin-ban-${u.user_id}`} onClick={() => ban(u)} className="rounded-full px-2.5 py-1 text-xs font-semibold border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><Ban size={11} /> {u.banned ? "Débannir" : "Bannir"}</button>
              </div>
            </div>
          ))}
          {!users.length && <p className="text-sm text-zinc-500 text-center py-8">{onlineOnly ? "Personne en ligne actuellement." : "Aucun utilisateur."}</p>}
        </div>
      )}
    </div>
  );
}

const AUDIT_ACTIONS = {
  set_tier: { emoji: "🎚️", label: "a changé le plan de" },
  ban: { emoji: "🚫", label: "a banni" },
  unban: { emoji: "✅", label: "a débanni" },
  grant: { emoji: "🎁", label: "a offert un accès à" },
  create_invite: { emoji: "💌", label: "a créé une invitation VIP pour" },
  creator_payout: { emoji: "💸", label: "a payé le créateur" },
  prospect_add: { emoji: "➕", label: "a ajouté le prospect" },
  prospect_import: { emoji: "📥", label: "a importé des prospects" },
  prospect_invite: { emoji: "📧", label: "a invité le prospect" },
  prospect_followups: { emoji: "🔁", label: "a lancé les relances" },
  prospect_goal: { emoji: "🎯", label: "a défini l'objectif de" },
  prospect_reassign: { emoji: "🔀", label: "a réassigné des prospects à" },
  commercial_add: { emoji: "👤", label: "a ajouté le commercial" },
  commercial_delete: { emoji: "🗑️", label: "a supprimé un commercial" },
};

const META_LABELS = {
  tier: "plan", days: "jours", reason: "motif", amount: "montant (€)",
  count: "nombre", tone: "ton", code: "code", sent: "envoyé", contacted: "contactés/sem",
  joined: "inscrits/sem", id: "id",
};

function AuditTab() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data.audit || [])).catch(() => setLogs([])); }, []);
  if (logs === null) return <Loader2 className="animate-spin text-pink-400 mx-auto mt-10" />;
  if (!logs.length) return <p className="text-sm text-zinc-500 text-center py-10">Aucune action enregistrée.</p>;
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-3">Journal des actions effectuées par les administrateurs (100 dernières).</p>
      <div className="space-y-2" data-testid="admin-audit-list">
        {logs.map((a, i) => {
          const def = AUDIT_ACTIONS[a.action] || { emoji: "•", label: a.action };
          const meta = a.meta || {};
          const chips = Object.entries(meta).filter(([, v]) => v !== null && v !== "" && v !== undefined);
          return (
            <div key={i} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid="admin-audit-row">
              <div className="flex items-start gap-2.5">
                <span className="text-lg leading-none mt-0.5">{def.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-semibold text-pink-300">{a.admin_email || "admin"}</span>
                    <span className="text-zinc-300"> {def.label} </span>
                    {a.target && <span className="font-semibold text-zinc-100 break-all">{a.target}</span>}
                  </p>
                  {chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {chips.map(([k, v]) => (
                        <span key={k} className="text-[11px] rounded-full bg-zinc-950 border border-zinc-800 px-2 py-0.5 text-zinc-400">
                          {META_LABELS[k] || k}: <span className="text-zinc-200 font-medium">{typeof v === "boolean" ? (v ? "oui" : "non") : String(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-600 mt-1.5">{fmt(a.at)} · {relTime(a.at)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SimpleList({ endpoint, field, render, empty }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data[field] || [])).catch(() => setItems([])); }, [endpoint, field]);
  if (items === null) return <Loader2 className="animate-spin text-pink-400 mx-auto mt-10" />;
  if (!items.length) return <p className="text-sm text-zinc-500 text-center py-10">{empty}</p>;
  return <div className="space-y-2">{items.map(render)}</div>;
}

function InvitesTab() {
  const [form, setForm] = useState({ name: "", email: "", tier: "docteur", days: 90, message: "" });
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/invites").then((r) => setInvites(r.data.invites || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nom et email requis"); return; }
    setBusy(true); vibrate();
    try {
      const res = await api.post("/admin/invites", { ...form, days: form.tier === "comp" ? 0 : Number(form.days) });
      toast.success(res.data.email_sent ? "Invitation envoyée par email 📧" : "Invitation créée (email non envoyé)");
      setForm({ name: "", email: "", tier: "docteur", days: 90, message: "" });
      load();
    } catch { toast.error("Échec"); } finally { setBusy(false); }
  };

  const resend = async (inv) => {
    vibrate();
    try {
      const res = await api.post(`/admin/invites/${inv.invite_id}/resend`);
      toast.success(res.data.email_sent ? "Email renvoyé 📧" : "Échec envoi email");
      load();
    } catch { toast.error("Échec"); }
  };

  const copyLink = async (inv) => {
    const url = `${APP_URL}/?vip=${inv.code}`;
    try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
    vibrate();
    setCopied(inv.code); toast.success("Lien copié");
    setTimeout(() => setCopied(null), 1500);
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Supprimer l'invitation de ${inv.name} ?`)) return;
    await api.delete(`/admin/invites/${inv.invite_id}`); load();
  };

  return (
    <div>
      <TabInfo title="🎁 Invitations VIP — offrir un accès gratuit">
        Pour <b className="text-zinc-300">chouchouter</b> influenceurs, presse ou partenaires : tu leur offres un accès premium
        (Pro / Docteur / illimité) pour une durée choisie. Ils reçoivent un lien, se connectent, et l'accès s'active tout seul.
        <b className="text-zinc-300"> Aucune commission, aucun code promo</b> — c'est juste un cadeau d'accès.
      </TabInfo>
      <form onSubmit={create} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3" data-testid="invite-form">
        <p className="text-sm font-bold">Inviter un VIP (influenceur, presse, partenaire)</p>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="invite-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500">
            <option value="pro">Pro</option>
            <option value="docteur">Docteur (accès total)</option>
            <option value="comp">Comp (illimité permanent)</option>
          </select>
          <input data-testid="invite-days" type="number" min="1" value={form.days} disabled={form.tier === "comp"}
            onChange={(e) => setForm({ ...form, days: e.target.value })} placeholder="Jours"
            className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500 disabled:opacity-40" />
        </div>
        <input data-testid="invite-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message perso (optionnel)" className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
        <p className="text-[11px] text-emerald-400/90">📧 L'email d'invitation part automatiquement à la création (avec le lien d'activation).</p>
        <button data-testid="invite-submit" disabled={busy} className="w-full rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 text-sm disabled:opacity-60">
          {busy ? "Création…" : "Créer l'invitation"}
        </button>
      </form>

      <div className="mt-4 space-y-2" data-testid="invite-list">
        {invites.map((inv) => (
          <div key={inv.invite_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`invite-${inv.code}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{inv.name} <span className="text-zinc-500 font-normal">· {inv.email}</span></p>
                <p className="text-xs text-zinc-500">
                  <span className={TIER_COLORS[inv.tier]}>{inv.tier}</span>
                  {inv.tier !== "comp" && ` · ${inv.days}j`}
                  {inv.status === "accepted"
                    ? <span className="text-emerald-400"> · accepté par {inv.claimed_by}</span>
                    : <span className="text-amber-400"> · en attente</span>}
                </p>
                <p className="text-[11px] mt-0.5">
                  {inv.email_sent
                    ? <span className="text-emerald-400/80">📧 email envoyé</span>
                    : <span className="text-zinc-600">📧 email non envoyé</span>}
                </p>
              </div>
              <button data-testid={`invite-resend-${inv.code}`} onClick={() => resend(inv)} className="rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1" title="Renvoyer l'email">
                <Send size={13} />
              </button>
              <button data-testid={`invite-copy-${inv.code}`} onClick={() => copyLink(inv)} className="rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1">
                {copied === inv.code ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />} Lien
              </button>
              <button onClick={() => revoke(inv)} className="text-zinc-600 hover:text-red-400 text-xs px-1">✕</button>
            </div>
          </div>
        ))}
        {!invites.length && <p className="text-sm text-zinc-500 text-center py-8">Aucune invitation.</p>}
      </div>
    </div>
  );
}

function CreatorsTab() {
  const [items, setItems] = useState([]);
  const [paying, setPaying] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/creators").then((r) => setItems(r.data.creators || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (c) => {
    if (!window.confirm(`Marquer ${(c.pending || 0).toFixed(2)}€ comme versé à ${c.name} (${c.code}) ?`)) return;
    setPaying(c.creator_id); vibrate();
    try { const r = await api.post(`/admin/creators/${c.creator_id}/mark-paid`); toast.success(`${(r.data.paid || 0).toFixed(2)}€ marqué payé`); load(); }
    catch { toast.error("Échec"); } finally { setPaying(null); }
  };

  const totalPending = items.reduce((s, c) => s + (c.pending || 0), 0);

  return (
    <div>
      <TabInfo title="🚀 Programme Créateurs — affiliation OUVERTE (commission)">
        Programme <b className="text-zinc-300">ouvert à tous & auto-approuvé</b> : n'importe qui rejoint en 10 s depuis <span className="font-mono">/createurs</span>.
        Le créateur <b className="text-zinc-300">gagne 50% le 1ᵉʳ mois + 25% à vie</b> sur chaque abonné ramené. Le filleul n'a <b className="text-zinc-300">aucune remise</b> (marge protégée).
        C'est le programme relié à tes <b className="text-zinc-300">commerciaux</b> (ils recrutent des créateurs et touchent l'override 10% + prime 50€). Versements manuels ici.
      </TabInfo>
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/25 p-4 mb-4 flex items-center gap-3">
        <Wallet size={20} className="text-emerald-400" />
        <div><p className="text-sm font-bold">{totalPending.toFixed(2)}€ à verser au total</p><p className="text-xs text-zinc-500">{items.length} créateur·ices · programme 50%/25%</p></div>
      </div>
      <div className="space-y-2" data-testid="admin-creators-list">
        {items.map((c) => (
          <div key={c.creator_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`admin-creator-${c.code}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{c.name} <span className="text-pink-400 font-mono">· {c.code}</span></p>
                <p className="text-xs text-zinc-500 truncate">{c.email} {c.payout_info ? `· 💳 ${c.payout_info}` : "· ⚠️ pas de coordonnées"}</p>
              </div>
              <button data-testid={`admin-creator-pay-${c.code}`} onClick={() => markPaid(c)} disabled={!c.pending || paying === c.creator_id}
                className="rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 px-3 py-1.5 text-xs font-bold disabled:opacity-40">
                {paying === c.creator_id ? "…" : `Payer ${(c.pending || 0).toFixed(0)}€`}
              </button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
              <span className="text-zinc-400"><b className="text-pink-300">{c.signups}</b> inscr.</span>
              <span className="text-zinc-400"><b className="text-emerald-300">{c.paying}</b> payants</span>
              <span className="text-zinc-400"><b className="text-sky-300">{(c.mrr || 0).toFixed(2)}€</b> MRR</span>
              <span className="text-zinc-400">gagné <b className="text-amber-300">{(c.total_earned || 0).toFixed(2)}€</b></span>
              <span className="text-zinc-400">versé <b>{(c.paid_out || 0).toFixed(2)}€</b></span>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-sm text-zinc-500 text-center py-8">Aucun créateur pour l'instant.</p>}
      </div>
    </div>
  );
}

function AmbassadorsTab() {
  const [form, setForm] = useState({ name: "", email: "", code: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ambassadors").then((r) => setItems(r.data.ambassadors || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const code = (form.code || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (!form.name.trim() || !form.email.trim() || code.length < 3) { toast.error("Nom, email et code (≥3) requis"); return; }
    setBusy(true); vibrate();
    try {
      const res = await api.post("/admin/ambassadors", { name: form.name.trim(), email: form.email.trim(), code });
      toast.success(res.data.email_sent ? "Ambassadrice créée & email envoyé 📧" : "Ambassadrice créée (email non envoyé)");
      setForm({ name: "", email: "", code: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  const resend = async (a) => {
    vibrate();
    try {
      const res = await api.post(`/admin/ambassadors/${a.ambassador_id}/resend`);
      toast.success(res.data.email_sent ? "Email renvoyé 📧" : "Échec envoi email");
      load();
    } catch { toast.error("Échec"); }
  };

  const copyLink = async (a) => {
    const url = `${APP_URL}/?promo=${a.code}`;
    try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
    vibrate();
    setCopied(a.code); toast.success("Lien copié");
    setTimeout(() => setCopied(null), 1500);
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'ambassadrice ${a.name} (${a.code}) ?`)) return;
    await api.delete(`/admin/ambassadors/${a.ambassador_id}`); load();
  };

  return (
    <div>
      <TabInfo title="👑 Ambassadrices — affiliation CURÉE (remise filleul)">
        Programme <b className="text-zinc-300">sur invitation</b> : TU choisis l'influenceuse et lui donnes un <b className="text-zinc-300">code promo perso</b> (ex. LEA).
        Ses filleul·es reçoivent <b className="text-zinc-300">-50% le 1ᵉʳ mois</b> (Pro & Docteur) — un argument fort pour son audience.
        Elle suit ses inscriptions / clientes payantes / MRR sur <span className="font-mono">/ambassadeur</span>.
        <br /><span className="text-amber-400/80">Différence avec « Créateurs » : ici le filleul a une remise (recrutement curé) ; côté Créateurs, c'est l'affilié qui touche une commission (recrutement ouvert).</span>
      </TabInfo>
      <form onSubmit={create} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3" data-testid="ambassador-form">
        <p className="text-sm font-bold">Créer une ambassadrice / influenceuse</p>
        <p className="text-[11px] text-zinc-500">Elle voit ses stats sur <span className="font-mono">/ambassadeur</span> (connectée avec cet email). Ses filleul·es ont -50% le 1ᵉʳ mois sur Pro & Docteur.</p>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="ambassador-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="ambassador-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (compte Google)" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
        </div>
        <input data-testid="ambassador-code" value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase() })}
          placeholder="Code promo (ex. LEA)" className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm font-mono tracking-widest outline-none focus:border-pink-500" />
        <button data-testid="ambassador-submit" disabled={busy} className="w-full rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 text-sm disabled:opacity-60">
          {busy ? "Création…" : "Créer l'ambassadrice"}
        </button>
      </form>

      <div className="mt-4 space-y-2" data-testid="ambassador-list">
        {items.map((a) => (
          <div key={a.ambassador_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`ambassador-row-${a.code}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{a.name} <span className="text-pink-400 font-mono">· {a.code}</span></p>
                <p className="text-xs text-zinc-500 truncate">{a.email}</p>
              </div>
              <button data-testid={`ambassador-resend-${a.code}`} onClick={() => resend(a)} className="rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1" title="Renvoyer l'email">
                <Send size={13} />
              </button>
              <button data-testid={`ambassador-copy-${a.code}`} onClick={() => copyLink(a)} className="rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1">
                {copied === a.code ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />} Lien
              </button>
              <button onClick={() => remove(a)} className="text-zinc-600 hover:text-red-400 text-xs px-1">✕</button>
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-zinc-400"><span className="font-bold text-pink-300">{a.signups}</span> inscriptions</span>
              <span className="text-zinc-400"><span className="font-bold text-emerald-300">{a.paying}</span> payantes</span>
              <span className="text-zinc-400"><span className="font-bold text-amber-300">{a.mrr} €</span> MRR</span>
              {a.email_sent ? <span className="text-emerald-400/80">📧 envoyé</span> : <span className="text-zinc-600">📧 non envoyé</span>}
            </div>
          </div>
        ))}
        {!items.length && <p className="text-sm text-zinc-500 text-center py-8">Aucune ambassadrice.</p>}
      </div>
    </div>
  );
}



const PLATFORMS_LOCAL = PLATFORMS;

function CopyBox({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch {}
    vibrate(); setCopied(true); toast.success("Copié ✓"); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <textarea readOnly value={text} data-testid={testid}
        className="w-full h-40 rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-sm text-zinc-200 resize-none outline-none" />
      <button data-testid={`${testid}-copy`} onClick={copy}
        className="absolute top-2 right-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1">
        {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />} Copier
      </button>
    </div>
  );
}

function ScriptsModal({ prospect, onClose }) {
  const [tone, setTone] = useState("cash");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="scripts-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareText size={18} className="text-pink-400" />
          <h3 className="font-display font-black">Scripts DM — {prospect.name || prospect.handle}</h3>
          <button onClick={onClose} className="ml-auto text-zinc-500 hover:text-white" data-testid="scripts-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">Copie-colle ce message en DM {prospect.platform ? `sur ${prospect.platform}` : ""}. Le lien inclus crédite « prospection » et amène direct sur la connexion créateur.</p>
        <div className="flex gap-2 mb-3">
          {[["cash", "Cash / jeune"], ["pro", "Pro / premium"]].map(([k, l]) => (
            <button key={k} data-testid={`scripts-tone-${k}`} onClick={() => setTone(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${tone === k ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <CopyBox text={buildDM(prospect, tone)} testid="scripts-dm" />
        <div className="mt-3 flex items-center justify-between rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5">
          <span className="text-xs text-zinc-500 truncate">{inviteLink(prospect.code)}</span>
        </div>
      </div>
    </div>
  );
}

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

function StatChip({ label, value, sub, color = "text-white" }) {
  return (
    <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-3 min-w-[92px]">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`font-display text-xl font-black mt-0.5 ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
    </div>
  );
}

function BreakdownTable({ title, rows }) {
  const entries = Object.entries(rows).filter(([, v]) => v.total > 0);
  if (!entries.length) return null;
  return (
    <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-3">
      <p className="text-xs font-bold text-zinc-300 mb-2">{title}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 text-left">
              <th className="font-medium py-1 pr-2"></th>
              <th className="font-medium py-1 px-2 text-right">Contactés</th>
              <th className="font-medium py-1 px-2 text-right">Ouverts</th>
              <th className="font-medium py-1 px-2 text-right">Inscrits</th>
              <th className="font-medium py-1 pl-2 text-right">Taux inscr.</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="border-t border-zinc-800/70">
                <td className="py-1.5 pr-2 font-semibold text-zinc-200 capitalize">{k}</td>
                <td className="py-1.5 px-2 text-right text-zinc-300">{v.contacted}</td>
                <td className="py-1.5 px-2 text-right text-sky-300">{v.opened}{v.contacted ? ` · ${pct(v.opened, v.contacted)}%` : ""}</td>
                <td className="py-1.5 px-2 text-right text-emerald-300">{v.joined}</td>
                <td className="py-1.5 pl-2 text-right font-bold text-pink-300">{pct(v.joined, v.contacted)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProspectStats({ stats }) {
  const g = stats.global;
  return (
    <div className="mb-4" data-testid="prospect-stats">
      <div className="flex flex-wrap gap-2 mb-3">
        <StatChip label="Prospects" value={g.total} />
        <StatChip label="Contactés" value={g.contacted} sub={`${g.relaunched} relancés`} color="text-amber-300" />
        <StatChip label="Ouverts" value={g.opened} sub={g.contacted ? `${pct(g.opened, g.contacted)}% ouverture` : ""} color="text-sky-300" />
        <StatChip label="Inscrits" value={g.joined} sub={g.contacted ? `${pct(g.joined, g.contacted)}% conv.` : ""} color="text-emerald-300" />
      </div>
      {!stats.open_tracking_active && (
        <p className="text-[11px] text-zinc-500 mb-3" data-testid="open-tracking-hint">
          💡 Le taux d'ouverture s'activera une fois le webhook Resend configuré (URL <span className="font-mono">/api/webhooks/resend</span> + « Open/Click tracking » activé dans Resend).
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <BreakdownTable title="Par commercial" rows={stats.by_commercial} />
        <BreakdownTable title="Par ton d'email" rows={stats.by_tone} />
      </div>
    </div>
  );
}

function GoalBar({ label, value, target, color }) {
  const p = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const done = target && value >= target;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-zinc-400">{label}</span>
        <span className={`font-semibold ${done ? "text-emerald-400" : "text-zinc-300"}`}>{value}/{target} {done && "✓"}</span>
      </div>
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: done ? "#34d399" : color }} />
      </div>
    </div>
  );
}

function GoalCard({ c, onSave }) {
  const [edit, setEdit] = useState(false);
  const [tc, setTc] = useState(c.target_contacted);
  const [tj, setTj] = useState(c.target_joined);
  return (
    <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`goal-${c.name}`}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-bold truncate">👤 {c.name}</p>
        <button data-testid={`goal-edit-${c.name}`} onClick={() => setEdit((e) => !e)} className="ml-auto text-[11px] text-pink-400 hover:text-pink-300">{edit ? "Annuler" : "Modifier"}</button>
      </div>
      {edit ? (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-zinc-400">Contactés/sem
            <input data-testid={`goal-tc-${c.name}`} type="number" min="0" value={tc} onChange={(e) => setTc(e.target.value)} className="w-20 rounded-lg bg-zinc-950 border border-zinc-800 px-2 py-1 text-sm outline-none focus:border-pink-500" />
          </label>
          <label className="flex items-center gap-2 text-xs text-zinc-400">Inscrits/sem
            <input data-testid={`goal-tj-${c.name}`} type="number" min="0" value={tj} onChange={(e) => setTj(e.target.value)} className="w-20 rounded-lg bg-zinc-950 border border-zinc-800 px-2 py-1 text-sm outline-none focus:border-pink-500" />
          </label>
          <button data-testid={`goal-save-${c.name}`} onClick={() => { onSave(c.name, Number(tc), Number(tj)); setEdit(false); }} className="rounded-full bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-4 py-1.5">Enregistrer</button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <GoalBar label="Contactés" value={c.contacted_week} target={c.target_contacted} color="#38bdf8" />
          <GoalBar label="Inscrits" value={c.joined_week} target={c.target_joined} color="#ec4899" />
        </div>
      )}
    </div>
  );
}

function ProspectGoals({ goals, onSave }) {
  const list = goals.commercials || [];
  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-4 mb-4" data-testid="prospect-goals">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-sm font-bold">🎯 Objectifs de la semaine</p>
        <span className="text-[11px] text-zinc-500">(défaut {goals.default.weekly_contacted} contactés / {goals.default.weekly_joined} inscrits · réinitialisé chaque lundi)</span>
      </div>
      {list.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => <GoalCard key={c.name} c={c} onSave={onSave} />)}
        </div>
      ) : (
        <p className="text-sm text-zinc-500">Ajoute des prospects avec un commercial assigné pour suivre leurs objectifs.</p>
      )}
    </div>
  );
}

function CommercialsPanel({ commercials, reload }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [en, setEn] = useState("");
  const [ee, setEe] = useState("");

  const add = async () => {
    if (!name.trim()) { toast.error("Nom requis"); return; }
    setBusy(true); vibrate();
    try { await api.post("/admin/commercials", { name, email }); toast.success(email ? "Commercial ajouté — email d'accès envoyé 📧" : "Commercial ajouté"); setName(""); setEmail(""); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  const startEdit = (c) => { setEditId(c.commercial_id); setEn(c.name); setEe(c.email || ""); };
  const saveEdit = async (c) => {
    try { await api.patch(`/admin/commercials/${c.commercial_id}`, { name: en, email: ee }); toast.success("Enregistré ✓"); setEditId(null); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Échec"); }
  };
  const toggleActive = async (c) => {
    try { await api.patch(`/admin/commercials/${c.commercial_id}`, { active: !c.active }); reload(); } catch { toast.error("Échec"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Supprimer ${c.name} ? Ses ${c.prospect_count} prospect·s seront détaché·s (non supprimés).`)) return;
    await api.delete(`/admin/commercials/${c.commercial_id}`); reload();
  };
  const [payingId, setPayingId] = useState(null);
  const markPaid = async (c) => {
    const pending = c.commission?.pending || 0;
    if (pending <= 0) { toast.info("Rien à verser"); return; }
    if (!window.confirm(`Marquer ${eur(pending)} comme versés à ${c.name} ?`)) return;
    setPayingId(c.commercial_id); vibrate();
    try { const r = await api.post(`/admin/commercials/${c.commercial_id}/mark-paid`); toast.success(`${eur(r.data.paid)} marqués versés ✓`); reload(); }
    catch { toast.error("Échec"); } finally { setPayingId(null); }
  };

  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-4 mb-4" data-testid="commercials-panel">
      <button data-testid="commercials-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2">
        <p className="text-sm font-bold">👥 Commerciaux</p>
        <span className="text-[11px] text-zinc-500">({commercials.length})</span>
        <ChevronRight size={16} className={`ml-auto text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="commercial-add-form">
            <input data-testid="commercial-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du commercial" className="flex-1 min-w-[140px] rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
            <input data-testid="commercial-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (pour son accès /pro)" className="flex-1 min-w-[160px] rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
            <button data-testid="commercial-submit" onClick={add} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold px-5 py-2.5 text-sm disabled:opacity-60">{busy ? "…" : "Ajouter"}</button>
          </div>
          <div className="space-y-1.5">
            {commercials.map((c) => (
              <div key={c.commercial_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-2.5" data-testid={`commercial-${c.commercial_id}`}>
                {editId === c.commercial_id ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    <input value={en} onChange={(e) => setEn(e.target.value)} className="flex-1 min-w-[120px] rounded-lg bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-sm outline-none" />
                    <input value={ee} onChange={(e) => setEe(e.target.value)} placeholder="email" className="flex-1 min-w-[140px] rounded-lg bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-sm outline-none" />
                    <button data-testid={`commercial-save-${c.commercial_id}`} onClick={() => saveEdit(c)} className="rounded-full bg-pink-500 text-white text-xs font-bold px-3 py-1.5">OK</button>
                    <button onClick={() => setEditId(null)} className="text-xs text-zinc-400">Annuler</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{c.name} {!c.active && <span className="text-[10px] text-zinc-500">(inactif)</span>}</p>
                      <p className="text-xs text-zinc-500 truncate">{c.email || "pas d'email"} · {c.prospect_count} prospect·s</p>
                    </div>
                    <button data-testid={`commercial-toggle-${c.commercial_id}`} onClick={() => toggleActive(c)} className={`text-[10px] font-bold rounded-full border px-2 py-0.5 ${c.active ? "text-emerald-300 border-emerald-500/30" : "text-zinc-400 border-zinc-700"}`}>{c.active ? "Actif" : "Inactif"}</button>
                    <button data-testid={`commercial-edit-${c.commercial_id}`} onClick={() => startEdit(c)} className="text-zinc-500 hover:text-white text-xs">Modifier</button>
                    <button data-testid={`commercial-delete-${c.commercial_id}`} onClick={() => del(c)} className="text-zinc-600 hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                )}
                {editId !== c.commercial_id && c.commission && (
                  <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center flex-wrap gap-2" data-testid={`commercial-commission-${c.commercial_id}`}>
                    <span className="text-[11px] rounded-full bg-zinc-800/80 px-2 py-0.5 text-zinc-300">{c.commission.creators_recruited} créa · {c.commission.paying_total} payants</span>
                    <span className="text-[11px] rounded-full bg-zinc-800/80 px-2 py-0.5 text-zinc-300">{eur(c.commission.override_monthly)}/mois</span>
                    {c.commission.bonus_count > 0 && <span className="text-[11px] rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-emerald-300">{c.commission.bonus_count} prime·s</span>}
                    <span className="text-[11px] text-zinc-500 ml-auto">à verser</span>
                    <span className="text-sm font-black text-emerald-400" data-testid={`commercial-pending-${c.commercial_id}`}>{eur(c.commission.pending)}</span>
                    <button data-testid={`commercial-pay-${c.commercial_id}`} onClick={() => markPaid(c)} disabled={payingId === c.commercial_id || c.commission.pending <= 0}
                      className="rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-bold px-3 py-1">{payingId === c.commercial_id ? "…" : "Payer"}</button>
                  </div>
                )}
              </div>
            ))}
            {!commercials.length && <p className="text-xs text-zinc-500 py-2">Aucun commercial. Ajoute-en un pour l'assigner à des prospects et lui donner un accès à son espace.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function ProspectsTab() {
  const [form, setForm] = useState({ name: "", platform: "TikTok", handle: "", email: "", followers: "", niche: "", commercial_id: "" });
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [csv, setCsv] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [scriptsFor, setScriptsFor] = useState(null);
  const [copied, setCopied] = useState(null);
  const [invitingId, setInvitingId] = useState(null);
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkTarget, setBulkTarget] = useState("");

  const load = useCallback((status = "") => {
    api.get(`/admin/prospects${status ? `?status=${status}` : ""}`).then((r) => { setItems(r.data.prospects || []); setCounts(r.data.counts || {}); }).catch(() => {});
    api.get("/admin/prospects/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/prospects/goals").then((r) => setGoals(r.data)).catch(() => {});
    api.get("/admin/commercials").then((r) => setCommercials(r.data.commercials || [])).catch(() => {});
  }, []);

  const saveGoal = async (name, contacted, joined) => {
    try { await api.post("/admin/prospects/goals", { commercial: name, weekly_contacted: contacted, weekly_joined: joined }); toast.success("Objectif enregistré ✓"); load(filter); }
    catch { toast.error("Échec"); }
  };
  useEffect(() => { load(filter); }, [load, filter]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.handle.trim()) { toast.error("Nom ou pseudo requis"); return; }
    setBusy(true); vibrate();
    try { await api.post("/admin/prospects", form); toast.success("Prospect ajouté"); setForm({ name: "", platform: "TikTok", handle: "", email: "", followers: "", niche: "", commercial_id: "" }); load(filter); }
    catch { toast.error("Échec"); } finally { setBusy(false); }
  };

  const reassignOne = async (p, commercial_id) => {
    try { await api.patch(`/admin/prospects/${p.prospect_id}`, { commercial_id }); load(filter); } catch { toast.error("Échec"); }
  };
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bulkReassign = async () => {
    if (!selected.length) return;
    try { const r = await api.post("/admin/prospects/reassign", { prospect_ids: selected, commercial_id: bulkTarget }); toast.success(`${r.data.updated} prospect·s réassigné·s`); setSelected([]); setBulkTarget(""); load(filter); }
    catch { toast.error("Échec"); }
  };

  const importCsv = async () => {
    const lines = csv.split("\n").map((l) => l.trim()).filter(Boolean);
    const itemsArr = lines.map((l) => {
      const [name, platform, handle, email, followers, niche] = l.split(",").map((s) => (s || "").trim());
      return { name, platform, handle, email, followers, niche };
    });
    if (!itemsArr.length) { toast.error("Colle au moins une ligne"); return; }
    setBusy(true);
    try { const r = await api.post("/admin/prospects/import", { items: itemsArr }); toast.success(`${r.data.imported} prospect·s importé·s`); setCsv(""); setShowCsv(false); load(filter); }
    catch { toast.error("Échec import"); } finally { setBusy(false); }
  };

  const invite = async (p, tone) => {
    setInvitingId(p.prospect_id); vibrate();
    try { const r = await api.post(`/admin/prospects/${p.prospect_id}/invite`, { tone }); toast.success(r.data.email_sent ? "Email envoyé 📧" : "Échec envoi email"); load(filter); }
    catch (err) { toast.error(err?.response?.data?.detail || "Échec"); } finally { setInvitingId(null); }
  };

  const setStatus = async (p, status) => {
    try { await api.patch(`/admin/prospects/${p.prospect_id}`, { status }); load(filter); } catch { toast.error("Échec"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.name || p.handle} ?`)) return;
    await api.delete(`/admin/prospects/${p.prospect_id}`); load(filter);
  };

  const copyLink = async (p) => {
    try { await navigator.clipboard.writeText(inviteLink(p.code)); } catch {}
    vibrate(); setCopied(p.prospect_id); toast.success("Lien copié"); setTimeout(() => setCopied(null), 1500);
  };

  const runFollowups = async () => {
    vibrate();
    try { const r = await api.post("/admin/prospects/run-followups"); toast.success(r.data.total ? `${r.data.total} relance·s envoyée·s (${r.data.relance_1}×J+3, ${r.data.relance_2}×J+7)` : "Aucune relance à envoyer pour l'instant"); load(filter); }
    catch { toast.error("Échec"); }
  };

  const pdfUrl = `${API}/creators/kit.pdf`;

  return (
    <div>
      {scriptsFor && <ScriptsModal prospect={scriptsFor} onClose={() => setScriptsFor(null)} />}

      {stats && <ProspectStats stats={stats} />}
      {goals && <ProspectGoals goals={goals} onSave={saveGoal} />}
      <CommercialsPanel commercials={commercials} reload={() => load(filter)} />

      {/* Bulk reassign toolbar */}
      {selected.length > 0 && (
        <div className="rounded-2xl bg-pink-500/10 border border-pink-500/30 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
          <span className="text-sm font-semibold text-pink-200">{selected.length} sélectionné·s</span>
          <select data-testid="bulk-commercial" value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)}
            className="rounded-full bg-zinc-950 border border-zinc-700 text-sm text-zinc-200 px-3 py-1.5 outline-none">
            <option value="">— Aucun (détacher) —</option>
            {commercials.map((c) => <option key={c.commercial_id} value={c.commercial_id}>{c.name}</option>)}
          </select>
          <button data-testid="bulk-reassign" onClick={bulkReassign} className="rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold px-4 py-1.5">Réassigner</button>
          <button data-testid="bulk-clear" onClick={() => setSelected([])} className="text-xs text-zinc-400 hover:text-white">Annuler</button>
        </div>
      )}

      {/* PDF + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="prospects-pdf"
          className="inline-flex items-center gap-1.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold px-4 py-2">
          <Download size={15} /> PDF à envoyer (WhatsApp)
        </a>
        <button data-testid="prospects-csv-toggle" onClick={() => setShowCsv((s) => !s)}
          className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold px-4 py-2">
          <Upload size={15} /> Import CSV
        </button>
        <button data-testid="prospects-run-followups" onClick={runFollowups}
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 text-sm font-semibold px-4 py-2">
          <Send size={15} /> Relancer (J+3 / J+7)
        </button>
        <div className="ml-auto flex gap-1.5">
          {[["", "Tous"], ["todo", "À faire"], ["contacted", "Contactés"], ["joined", "Inscrits"]].map(([k, l]) => (
            <button key={k} data-testid={`prospects-filter-${k || "all"}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${filter === k ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>
              {l}{k && counts[k] ? ` (${counts[k]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {showCsv && (
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 mb-4" data-testid="prospects-csv-box">
          <p className="text-sm font-bold mb-1">Import CSV</p>
          <p className="text-[11px] text-zinc-500 mb-2">Une ligne par prospect : <span className="font-mono">nom,plateforme,pseudo,email,abonnés,niche</span></p>
          <textarea data-testid="prospects-csv-input" value={csv} onChange={(e) => setCsv(e.target.value)}
            placeholder={"Lea Style,TikTok,@leastyle,lea@mail.com,120k,lifestyle\nMax Dating,Instagram,@maxdating,,45k,dating"}
            className="w-full h-28 rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-sm resize-none outline-none focus:border-pink-500" />
          <button data-testid="prospects-csv-import" onClick={importCsv} disabled={busy}
            className="mt-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold px-4 py-2 text-sm disabled:opacity-60">Importer</button>
        </div>
      )}

      {/* Add form */}
      <form onSubmit={add} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3 mb-4" data-testid="prospect-form">
        <p className="text-sm font-bold">Ajouter un prospect</p>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="prospect-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom / pseudo public" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <select data-testid="prospect-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input data-testid="prospect-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@pseudo" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="prospect-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (pour l'invitation)" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="prospect-followers" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} placeholder="Abonnés (ex 120k)" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <input data-testid="prospect-niche" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="Niche (dating, lifestyle…)" className="rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
        </div>
        <select data-testid="prospect-commercial" value={form.commercial_id} onChange={(e) => setForm({ ...form, commercial_id: e.target.value })} className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500">
          <option value="">— Commercial assigné (optionnel) —</option>
          {commercials.map((c) => <option key={c.commercial_id} value={c.commercial_id}>{c.name}</option>)}
        </select>
        <button data-testid="prospect-submit" disabled={busy} className="w-full rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 text-sm disabled:opacity-60">{busy ? "…" : "Ajouter le prospect"}</button>
      </form>

      {/* List */}
      <div className="space-y-2" data-testid="prospects-list">
        {items.map((p) => {
          const sm = STATUS_META[p.status] || STATUS_META.todo;
          return (
            <div key={p.prospect_id} className="rounded-xl bg-zinc-900 border border-zinc-800 p-3" data-testid={`prospect-${p.code}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="checkbox" data-testid={`prospect-select-${p.code}`} checked={selected.includes(p.prospect_id)} onChange={() => toggleSel(p.prospect_id)}
                  className="w-4 h-4 accent-pink-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{p.name || p.handle} {p.handle && p.name && <span className="text-zinc-500 font-normal">{p.handle.startsWith("@") ? p.handle : `@${p.handle}`}</span>}</p>
                  <p className="text-xs text-zinc-500 truncate">
                    {p.platform || "—"}{p.followers ? ` · ${p.followers}` : ""}{p.niche ? ` · ${p.niche}` : ""}{p.email ? ` · ${p.email}` : " · pas d'email"}
                    {p.commercial ? ` · 👤 ${p.commercial}` : " · 👤 non assigné"}
                  </p>
                </div>
                <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 ${sm.c}`}>{sm.label}</span>
              </div>
              {(p.relance_1_at || p.relance_2_at) && (
                <p className="text-[10px] text-amber-400/80 mt-1" data-testid={`prospect-relance-${p.code}`}>
                  🔁 Relancé {p.relance_2_at ? "2×" : "1×"}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button data-testid={`prospect-email-cash-${p.code}`} onClick={() => invite(p, "cash")} disabled={!p.email || invitingId === p.prospect_id}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-pink-500/30 text-pink-300 hover:bg-pink-500/10 flex items-center gap-1 disabled:opacity-40">
                  {invitingId === p.prospect_id ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Email cash
                </button>
                <button data-testid={`prospect-email-pro-${p.code}`} onClick={() => invite(p, "pro")} disabled={!p.email || invitingId === p.prospect_id}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1 disabled:opacity-40">
                  <Mail size={11} /> Email pro
                </button>
                <button data-testid={`prospect-scripts-${p.code}`} onClick={() => setScriptsFor(p)}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1">
                  <MessageSquareText size={11} /> Scripts DM
                </button>
                <button data-testid={`prospect-copy-${p.code}`} onClick={() => copyLink(p)}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1">
                  {copied === p.prospect_id ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />} Lien
                </button>
                <select data-testid={`prospect-status-${p.code}`} value={p.status} onChange={(e) => setStatus(p, e.target.value)}
                  className="rounded-full bg-zinc-950 border border-zinc-700 text-xs text-zinc-300 px-2 py-1 outline-none">
                  <option value="todo">À contacter</option>
                  <option value="contacted">Contacté</option>
                  <option value="joined">Inscrit</option>
                </select>
                <select data-testid={`prospect-reassign-${p.code}`} value={p.commercial_id || ""} onChange={(e) => reassignOne(p, e.target.value)}
                  className="rounded-full bg-zinc-950 border border-zinc-700 text-xs text-zinc-300 px-2 py-1 outline-none max-w-[130px]">
                  <option value="">👤 Non assigné</option>
                  {commercials.map((c) => <option key={c.commercial_id} value={c.commercial_id}>{c.name}</option>)}
                </select>
                <button data-testid={`prospect-delete-${p.code}`} onClick={() => remove(p)} className="text-zinc-600 hover:text-red-400 px-1"><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
        {!items.length && <p className="text-sm text-zinc-500 text-center py-10">Aucun prospect. Ajoute-en un ou importe une liste 👆</p>}
      </div>
    </div>
  );
}
